import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, RefreshCw, ChevronDown, ChevronUp, ListChecks, Sparkles,
  AlertTriangle, TrendingUp, Loader2, Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import MaturityGauge from '../components/MaturityGauge';
import MaturityAssessment from '../components/MaturityAssessment';
import { getMaturityBand } from '../lib/utils';
import { downloadAuthedFile } from '../lib/fileDownload';
import PageHelpButton from '../components/PageHelpButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function IndicatorPill({ on, label }) {
  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
        on ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
           : 'bg-slate-50 text-slate-500 border border-slate-200'
      }`}
      data-testid={`maturity-indicator-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {label}
    </div>
  );
}

function PillarRow({ pillar, expanded, onToggle }) {
  const band = getMaturityBand(pillar.score);
  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`maturity-pillar-${pillar.pillar_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 py-3 px-1 hover:bg-slate-50 transition text-left"
      >
        <div className="w-1 self-stretch rounded-full" style={{ background: band.color }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800 truncate">{pillar.name}</div>
          <div className="text-xs text-slate-500">Weight {Math.round(pillar.weight * 100)}%</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-lg font-bold text-[#0F2040] tabular-nums">{Math.round(pillar.score)}</div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: band.color }}>{band.label}</div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div className="bg-slate-50/60 px-4 py-3 space-y-1.5">
          {(pillar.dimensions || []).map((d) => (
            <div key={d.dimension_id} className="flex items-center justify-between text-xs">
              <span className="text-slate-600">{d.name}</span>
              <span className="font-mono text-slate-700">L{d.level} · {Math.round(d.score)}/100</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MaturityHealthPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(params.get('organization_id') || '');
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [expandedPillar, setExpandedPillar] = useState(null);

  // Load orgs list
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/organizations?limit=200`, { withCredentials: true });
        setOrgs(r.data?.organizations || []);
      } catch (err) {
        console.warn('orgs load failed', err);
      }
    })();
  }, []);

  const loadInsights = useCallback(async (id) => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/maturity/insights${id ? `?organization_id=${encodeURIComponent(id)}` : ''}`;
      const r = await axios.get(url, { withCredentials: true });
      setInsights(r.data);
    } catch (err) {
      console.error('maturity insights load failed', err);
      setInsights({ has_assessment: false });
      toast.error('Could not load maturity insights.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInsights(orgId); }, [orgId, loadInsights]);

  const selectedOrg = orgs.find((o) => o.org_id === orgId);
  const hasData = insights?.has_assessment;
  const band = hasData ? getMaturityBand(insights.overall_score) : null;

  const handleSelectOrg = (val) => {
    setOrgId(val);
    setParams(val ? { organization_id: val } : {});
  };

  const onComplete = ({ assessment }) => {
    setWizardOpen(false);
    loadInsights(orgId);
    // toast already fired in wizard
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="maturity-health-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#0F2040] tracking-tight flex items-center gap-2">
            Organization Maturity
            <PageHelpButton section="5-the-maturity-dashboard" label="Maturity — open the user guide" />
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl text-sm">
            Score a customer's learning maturity across 5 pillars and 30 dimensions.
            The score baselines Readiness Factors on new estimates and appears in the Design Pack.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={orgId || 'all'} onValueChange={(v) => handleSelectOrg(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[260px]" data-testid="maturity-org-select">
              <Building2 className="w-4 h-4 mr-1.5 text-slate-400" />
              <SelectValue placeholder="Pick a customer organization…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">My latest assessment (any org)</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => loadInsights(orgId)}
            data-testid="maturity-refresh"
            title="Reload"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {hasData && orgId ? (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await downloadAuthedFile(
                    `/api/maturity/briefing/pdf?organization_id=${encodeURIComponent(orgId)}`,
                    'maturity_briefing.pdf',
                  );
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Could not download the briefing PDF.');
                }
              }}
              data-testid="maturity-download-briefing"
              title="Download a customer-facing one-page briefing PDF"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Briefing PDF
            </Button>
          ) : null}
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-[#0F2040] hover:bg-[#1a2f5a]"
            data-testid="maturity-take-assessment"
          >
            <ListChecks className="w-4 h-4 mr-1.5" />
            {hasData ? 'Re-assess' : 'Take assessment'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : !hasData ? (
        <Card className="border-dashed border-2 border-slate-200" data-testid="maturity-empty">
          <CardContent className="py-16 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-cyan-400 mb-3" />
            <div className="text-lg font-semibold text-[#0F2040]">No assessment yet</div>
            <p className="text-sm text-slate-500 max-w-md mx-auto mt-1">
              {selectedOrg ? `Score ${selectedOrg.name}` : 'Score this customer'} across 30 questions (about 6 minutes)
              to generate the maturity baseline.
            </p>
            <Button
              onClick={() => setWizardOpen(true)}
              className="mt-4 bg-[#0F2040] hover:bg-[#1a2f5a]"
              data-testid="maturity-empty-start"
            >
              Start assessment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Gauge card */}
          <Card className="lg:col-span-1" data-testid="maturity-score-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-wide text-slate-500">Maturity Score</CardTitle>
            </CardHeader>
            <CardContent>
              <MaturityGauge
                score={insights.overall_score}
                pillarScores={insights.pillar_scores}
                pillarTrends={insights.pillar_trends}
                overallTrend={insights.overall_trend}
                label={insights.organization_name ? `for ${insights.organization_name}` : null}
              />
              <p className="text-xs text-slate-500 mt-4 text-center max-w-xs mx-auto">{band.description}</p>
              {insights.created_at ? (
                <div className="mt-2 text-xs text-slate-400 text-center">
                  Last assessed {new Date(insights.created_at).toLocaleDateString()}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Band insights */}
          <Card className="lg:col-span-2" data-testid="maturity-band-insights">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-wide text-slate-500">
                What this band means
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Structural capability</div>
                  <p className="text-slate-700">{insights.band_insights.structural_capability}</p>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Financial realization</div>
                  <p className="text-slate-700">{insights.band_insights.financial_realization}</p>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Risk profile</div>
                  <p className="text-slate-700">{insights.band_insights.risk_profile}</p>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Strategic positioning</div>
                  <p className="text-slate-700">{insights.band_insights.strategic_positioning}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                <IndicatorPill on={insights.indicators?.process_maturity} label="Process maturity" />
                <IndicatorPill on={insights.indicators?.roi_predictability} label="ROI predictability" />
                <IndicatorPill on={insights.indicators?.executive_visibility} label="Executive visibility" />
                <IndicatorPill on={insights.indicators?.strategic_integration} label="Strategic integration" />
              </div>

              {insights.uplift?.next_band && (
                <div className="flex items-center gap-3 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2 mt-2">
                  <TrendingUp className="w-4 h-4 text-cyan-700" />
                  <div className="text-sm text-cyan-900">
                    Reaching <span className="font-semibold">{insights.uplift.next_band}</span> closes another
                    <span className="font-bold mx-1">{insights.uplift.roi_improvement_pct}</span>
                    percentage points of ROI leakage.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Primary constraint */}
          {insights.primary_constraint && (
            <Card className="lg:col-span-3 border-amber-200 bg-amber-50/30" data-testid="maturity-constraint">
              <CardHeader className="pb-2 flex flex-row items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-base text-amber-900">
                  Primary constraint: {insights.primary_constraint.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-amber-900">
                  Scoring <span className="font-bold">{Math.round(insights.primary_constraint.score)}/100</span>
                  &nbsp;— this is the lowest pillar and where investment will compound most.
                </div>
                {insights.primary_constraint.symptoms?.length ? (
                  <div className="mt-2">
                    <div className="text-xs uppercase tracking-wide text-amber-700 mb-1">Weak dimensions</div>
                    <ul className="text-xs text-amber-900 list-disc list-inside space-y-0.5">
                      {insights.primary_constraint.symptoms.map((s) => <li key={s}>{s}</li>)}
                    </ul>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Pillar breakdown */}
          <Card className="lg:col-span-3" data-testid="maturity-pillar-breakdown">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-wide text-slate-500">Pillar breakdown</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(insights.pillar_scores || []).map((p) => (
                <PillarRow
                  key={p.pillar_id}
                  pillar={p}
                  expanded={expandedPillar === p.pillar_id}
                  onToggle={() => setExpandedPillar(expandedPillar === p.pillar_id ? null : p.pillar_id)}
                />
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedOrg ? `Maturity assessment · ${selectedOrg.name}` : 'Maturity assessment'}
            </DialogTitle>
            <DialogDescription>
              Pick the statement that best describes the customer today. You can save and resume — answers
              persist locally until you submit.
            </DialogDescription>
          </DialogHeader>
          <MaturityAssessment
            organizationId={orgId || null}
            organizationName={selectedOrg?.name || null}
            industry={selectedOrg?.industry || null}
            onComplete={onComplete}
            onCancel={() => setWizardOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
